import React, { useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Header from './Header';
import confetti from 'canvas-confetti';
import './IntegrationGuide.css';
import SiteFooter from './SiteFooter';

const IntegrationGuide: React.FC = () => {
  // Function to update header height CSS variable
  const updateHeaderHeight = useCallback(() => {
    const header = document.querySelector('header');
    if (header) {
      const height = header.offsetHeight;
      document.documentElement.style.setProperty('--header-height', `${height}px`);
    }
  }, []);

  useEffect(() => {
    const initialTimeout = setTimeout(() => {
      updateHeaderHeight();
    }, 100);
    
    const header = document.querySelector('header');
    if (header) {
      const resizeObserver = new ResizeObserver(() => {
        updateHeaderHeight();
      });
      
      resizeObserver.observe(header);
      window.addEventListener('resize', updateHeaderHeight);
      
      return () => {
        clearTimeout(initialTimeout);
        resizeObserver.disconnect();
        window.removeEventListener('resize', updateHeaderHeight);
      };
    } else {
      return () => {
        clearTimeout(initialTimeout);
      };
    }
  }, [updateHeaderHeight]);

  return (
    <div className="integration-guide-page">
      <Header subtitle="Overview" showNavigation={true} />
      
      <main className="container">
        <div className="main-content">
          <div className="page-header">
            <h1>How to launch your trustlined service</h1>
            <p className="page-subtitle">
              The 5 steps to launch a trustlined version of an existing smart contract.
            </p>
          </div>

          <div className="welcome-block">
            <p>
              Welcome to Trustline. Trustline is a permission layer for smart contracts.<br/><br/> 
              Trustline adds pre-transaction verifications to any smart contract call, to increase security against malicious calls to admin functions and control over users functions. Trustline works with all the smart contracts with a single line of code - currently on EVM.<br/><br/>This overview and quick start will walk you through the 5 easy steps to make any smart contract of your choice deployed with a new user authentication via email OTP.
            </p>
          </div>

          <div className="steps-container">
            <div className="step-card">
              <div className="step-number">1</div>
              <div className="step-content">
                <h2>Add Trustline to your smart contract</h2>
                <p>
                  Start from your own smart contract or pick one from the shelf.
                  The EVM SDK NPM package protects it with just 1 line of code.
                </p>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">2</div>
              <div className="step-content">
                <h2>Select your Blockchain</h2>
                <p>
                Select the appropriate Validation Engine address based on your target blockchain and use it in your smart contract deployment.
                </p>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">3</div>
              <div className="step-content">
                <h2>Set your policies and activate protection</h2>
                <p>
                  Once you contract is deployed, start the onboarding on the onboarding portal.
                </p>
              </div>
            </div>

            <div className="step-card">
              <div className="step-number">4</div>
              <div className="step-content">
                <h2>Connect Trustline to your UI</h2>
                <p>
                  Trigger transactions validation from your (or anyone's) UI in a few lines of code using the Web SDK NPM package.
                </p>
              </div>
            </div>

            <div 
              className="step-card"
              onClick={() => {
                confetti({
                  particleCount: 100,
                  spread: 70,
                  origin: { y: 0.6 }
                });
              }}
              style={{ cursor: 'pointer' }}
            >
              <div className="step-number">5</div>
              <div className="step-content">
                <h2>And... voila!</h2>
                <p>
                  Share your UI with your users, your smart contract service is protected by Trustline.
                </p>
              </div>
            </div>
          </div>

          <div className="quick-start-cta">
            <Link 
              to="/quick-start-guide" 
              className="resource-card recommended"
            >
              <div className="recommended-banner">Next step</div>
              <h3>Quick Start</h3>
              <p>Start from your existing smart contract, and deploy a first trustlined version with just what you need to know from the quick start guide.</p>
              <div className="resource-link">
                View Quick Start {'>'}
              </div>
            </Link>
          </div>

          <div className="resources-section">
            <h2>Other resources</h2>
            <div className="resources-grid">
              <a 
                href="https://github.com/TrustLine-id/sdk-example" 
                target="_blank" 
                rel="noopener noreferrer"
                className="resource-card"
              >
                <h3>Full Integration Example</h3>
                <p>
                  Explore a ready-to-deploy example including UI and smart contract integration 
                  in our GitHub monorepo.
                </p>
                <div className="resource-link">
                  View on GitHub {'>'}
                </div>
              </a>
              
              <a 
                href="https://github.com/Trustline-id/evmsdk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="resource-card"
              >
                <h3>EVMSDK GitHub</h3>
                <p>
                  View the source code, contribute, and explore the EVM SDK repository on GitHub.
                </p>
                <div className="resource-link">
                  View on GitHub {'>'}
                </div>
              </a>
              
              <a 
                href="https://github.com/Trustline-id/websdk" 
                target="_blank" 
                rel="noopener noreferrer"
                className="resource-card"
              >
                <h3>WebSDK GitHub</h3>
                <p>
                  View the source code, contribute, and explore the Web SDK repository on GitHub.
                </p>
                <div className="resource-link">
                  View on GitHub {'>'}
                </div>
              </a>
            </div>
          </div>

          <div className="additional-info-section">
            <p>
              For additional information regarding implementation addresses, onboarding configuration, and policy setup, visit our{' '}
              <Link to="/resources" className="inline-link">
                Resources page
              </Link>
              .
            </p>
          </div>
        </div>
      </main>
      
      <SiteFooter />
    </div>
  );
};

export default IntegrationGuide;

